import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = categories.find((c) => c.slug === article?.categorySlug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-20 text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-2xl mb-4">
            <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
          </div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">你访问的文章不存在或已被删除</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `https://chiguawang.com/article/${slug}/#article`,
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.coverImage,
        articleSection: article.category,
        keywords: article.tags.join(', '),
        url: `https://chiguawang.com/article/${slug}`,
        publisher: {
          '@type': 'Organization',
          name: '吃瓜网',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://chiguawang.com/article/${slug}/#webpage`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://chiguawang.com' },
          ...(category
            ? [
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: category.name,
                  item: `https://chiguawang.com/category/${category.slug}`,
                },
              ]
            : []),
          {
            '@type': 'ListItem',
            position: category ? 3 : 2,
            name: article.title,
            item: `https://chiguawang.com/article/${slug}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Breadcrumb */}
        <div className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 py-3 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
                  首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                {category && (
                  <>
                    <Link
                      to={`/category/${category.slug}`}
                      className="hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      {category.name}
                    </Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </>
                )}
                <span className="text-foreground-900 font-medium truncate max-w-[300px]">
                  {article.title}
                </span>
              </nav>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-4xl mx-auto">
            {/* Cover Image */}
            <div className="relative rounded-2xl overflow-hidden mb-8 h-[300px] md:h-[420px]">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>

            {/* Meta */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer"
                  >
                    {category.name}
                  </Link>
                )}
                {article.isHot && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                    <i className="ri-fire-line mr-1"></i>热门
                  </span>
                )}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 阅读
                </span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-8">
              {article.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 border border-background-200"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Content */}
            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed
                prose-p:mb-5 prose-p:text-[15px] prose-p:leading-[1.85]
                prose-strong:text-foreground-900 prose-strong:font-semibold"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-xl">
                  <i className="ri-links-line text-secondary-600 text-xl"></i>
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  相关文章
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mt-2">
                        {rel.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-3 text-xs text-foreground-400">
                        <span>{rel.publishDate}</span>
                        <span>{formatViews(rel.views)} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}